import React, { memo, MutableRefObject } from 'react';

import locator from 'utils/locator';
import { getSearchValueFromSearchQuery } from 'utils/searchQuery';

import { SearchBox } from '@johnlewispartnership/wtr-ingredients/ingredients/SearchBox';
import autoSuggestClear from 'redux/modules/auto-suggest/actions/auto-suggest-clear';
import autoSuggestSearch from 'redux/modules/auto-suggest/actions/auto-suggest-search';
import { updateSearchTerm } from 'redux/modules/search/actions/update-search-term';
import { useWtrSelector } from 'redux/hooks';
import { useOnClickOutside } from 'hooks';
import { useClientSideMediaQuery } from 'hooks/use-client-side-media-query';
import { useShopByList } from 'hooks/use-shop-by-list';

import { getAutoSuggestActive } from 'redux/modules/auto-suggest/selectors';
import { getFunctionalCookieState } from 'redux/modules/cookies/selectors';

import AutoComplete from 'components/Search/AutoComplete';
import SearchHistory from 'components/Search/SearchHistory';
import ShopByListCta from 'components/ShopByListCta';
import MultiSearchModal from 'components/MultiSearch/Modal';
import { BREAKPOINTS } from 'constants/grid';

import { useSearchForm } from './use-search-form';

type Props = {
  homepage: boolean;
  id?: string;
  onSearch: (() => WtrAction) | null;
  placeholder: string;
  className?: string;
  historyClassName?: string;
  autocompleteClassName?: string;
};

const SearchForm = memo(
  ({
    homepage = false,
    id = undefined,
    onSearch = null,
    placeholder = 'Search...',
    className = '',
    historyClassName = '',
    autocompleteClassName = '',
  }: Props) => {
    const autoSuggestActive = useWtrSelector(getAutoSuggestActive);
    const functionalCookieConsent = useWtrSelector(getFunctionalCookieState);
    const isMobileOrTablet = useClientSideMediaQuery({ maxWidth: BREAKPOINTS.sm });
    const showShopByList = useShopByList();

    const initialValue =
      (!locator.search.includes('searchType') && getSearchValueFromSearchQuery(locator.search)) ||
      '';

    const {
      handleChange,
      handleClear,
      handleFocus,
      handleBlur,
      handleSelectedAutoCompleteItem,
      handleSelectedSearchHistoryItem,
      handleSubmit,
      hasFocus,
      inputRef,
      insideRef,
      showSearchHistory,
      value,
    } = useSearchForm({
      autoSuggestActive,
      eligibleForAutoSuggest: true,
      functionalCookieConsent,
      initialValue,
      onChange: autoSuggestSearch,
      onClear: autoSuggestClear,
      onSubmit: updateSearchTerm,
      onSearch,
    });

    const isNonEmptyValue = value.length > 0;
    const hideShopByListCta = isNonEmptyValue;
    const hideMultiSearchCta = homepage || isNonEmptyValue;

    const inputProps = {
      onFocus: handleFocus,
      ref: inputRef,
    };

    useOnClickOutside(insideRef, () => {
      const hasSelectedText = window.getSelection()?.toString().length;
      if (!hasSelectedText) handleBlur();
    });

    return (
      <SearchBox
        ariaActivedescendant={showSearchHistory ? 'search-history-item' : 'autocomplete-suggestion'}
        ariaAutocomplete="list"
        ariaOwns={showSearchHistory ? 'search-history' : 'autocomplete-listbox'}
        focus={hasFocus}
        formRef={insideRef}
        id={id}
        initialValue={initialValue}
        inputProps={inputProps}
        onChange={handleChange}
        onClearClick={handleClear}
        onSubmit={handleSubmit}
        placeholder={placeholder}
        value={value}
        className={className}
      >
        {hasFocus && !showSearchHistory ? (
          <AutoComplete
            homepage={homepage}
            inputRef={inputRef as MutableRefObject<HTMLInputElement>}
            onClear={autoSuggestClear}
            onSelectItem={handleSelectedAutoCompleteItem}
            className={autocompleteClassName}
          />
        ) : null}
        {showSearchHistory ? (
          <SearchHistory
            inputRef={inputRef as MutableRefObject<HTMLInputElement>}
            homepage={homepage}
            onSelectTerm={handleSelectedSearchHistoryItem}
            className={historyClassName}
          />
        ) : null}
        {showShopByList ? (
          <ShopByListCta hidden={hideShopByListCta} searchbox />
        ) : (
          <MultiSearchModal hidden={hideMultiSearchCta} mobile={isMobileOrTablet} header />
        )}
      </SearchBox>
    );
  },
);

SearchForm.displayName = 'SearchForm';

export default SearchForm;
